// apps/frontend/src/lib/achievements.ts
// Système d'achievements one-shot : confetti + toast Glowp à des moments
// émotionnels clés (1ère annonce publiée, 1er favori posé, 1ère vente, etc.).
// Chaque achievement n'est déclenché qu'UNE seule fois par user — on
// persiste un flag en localStorage pour ne pas spammer.

export type AchievementKey =
  | 'first_listing_published'
  | 'first_favorite_added'
  | 'first_message_sent'
  | 'first_sale_received'
  | 'tenth_listing_published';

interface AchievementConfig {
  /** Titre court montré dans le toast */
  title: string;
  /** Message de soutien sous le titre */
  message: string;
}

const CONFIG: Record<AchievementKey, AchievementConfig> = {
  first_listing_published: {
    title: 'Première pièce en ligne',
    message: 'Bravo, tu fais maintenant partie des vendeurs Glowp.',
  },
  first_favorite_added: {
    title: 'Premier coup de cœur',
    message: 'Retrouve toujours tes favoris depuis le menu.',
  },
  first_message_sent: {
    title: 'Premier contact lancé',
    message: 'Une vente commence souvent par un message.',
  },
  first_sale_received: {
    title: 'Première vente !',
    message: 'Une étape énorme. Continue, ça ne fait que commencer.',
  },
  tenth_listing_published: {
    title: '10 pièces publiées',
    message: 'Ton catalogue commence vraiment à exister.',
  },
};

const STORAGE_PREFIX = 'glowp_achievement_';

function alreadyTriggered(key: AchievementKey): boolean {
  try {
    return !!localStorage.getItem(STORAGE_PREFIX + key);
  } catch {
    return false;
  }
}

function markTriggered(key: AchievementKey) {
  try {
    localStorage.setItem(STORAGE_PREFIX + key, String(Date.now()));
  } catch {
    /* localStorage indispo */
  }
}

// Confetti palette Glowp (beige/doré). On lance 2 bursts décalés en X
// pour un effet plus large + une pluie centrée plus discrète.
// Exporté pour réutilisation hors du système d'achievements (winner enchère).
export async function fireConfetti() {
  // canvas-confetti (~24 KB) importé à l'usage — la lib ne charge qu'au
  // moment d'une vraie célébration, pas au démarrage de l'app.
  const confetti = (await import('canvas-confetti')).default;
  const palette = ['#C8A882', '#E8DAC7', '#B89968', '#8B6914', '#F5EFE6'];
  const common = {
    spread: 70,
    startVelocity: 35,
    scalar: 0.95,
    ticks: 200,
    colors: palette,
  } as const;

  confetti({
    ...common,
    particleCount: 50,
    origin: { x: 0.25, y: 0.7 },
    angle: 60,
  });
  confetti({
    ...common,
    particleCount: 50,
    origin: { x: 0.75, y: 0.7 },
    angle: 120,
  });
  // Petite pluie de finition
  setTimeout(() => {
    confetti({
      ...common,
      particleCount: 25,
      origin: { x: 0.5, y: 0.4 },
      spread: 120,
      startVelocity: 25,
    });
  }, 250);
}

export interface AchievementToast {
  title: string;
  message: string;
}

type ToastHandler = (toast: AchievementToast) => void;

// Le composant App.tsx peut s'enregistrer pour afficher la notification.
// Pas de dépendance directe sur un système de toast existant — on reste
// agnostique pour ne rien casser.
let toastHandler: ToastHandler | null = null;

export function setAchievementToastHandler(handler: ToastHandler | null) {
  toastHandler = handler;
}

/**
 * Déclenche un achievement s'il n'a pas déjà été marqué pour cet utilisateur
 * (sur ce device). Confetti + toast Glowp. Idempotent — appel suivant = no-op.
 */
export function triggerAchievement(key: AchievementKey): void {
  if (typeof window === 'undefined') return;
  if (alreadyTriggered(key)) return;
  markTriggered(key);

  fireConfetti();
  const cfg = CONFIG[key];
  toastHandler?.({ title: cfg.title, message: cfg.message });
}

/**
 * Reset un achievement spécifique — utile pour tester ou si on veut
 * permettre à l'user de "relancer" l'expérience.
 */
export function resetAchievement(key: AchievementKey): void {
  try {
    localStorage.removeItem(STORAGE_PREFIX + key);
  } catch {
    /* localStorage indispo */
  }
}

// ============================================================================
// CATALOGUE PUBLIC DES ACHIEVEMENTS — pour afficher la liste dans le profile
// (débloqués + à débloquer). Inclut icône, description plus détaillée,
// "comment débloquer" et état actuel pour l'user.
// ============================================================================

export interface AchievementPublic extends AchievementConfig {
  key: AchievementKey;
  /** Phrase d'incitation pour débloquer */
  howTo: string;
  /** Lucide icon name (en string pour rester serializable) */
  icon: 'Sparkles' | 'Heart' | 'MessageCircle' | 'ShoppingBag' | 'Trophy';
  /** True si débloqué pour cet user (sur ce device) */
  unlocked: boolean;
  /** Date d'unlock (ISO) si débloqué */
  unlockedAt: string | null;
}

const PUBLIC_META: Record<
  AchievementKey,
  { howTo: string; icon: AchievementPublic['icon'] }
> = {
  first_listing_published: {
    howTo: 'Publie ta toute première annonce.',
    icon: 'Sparkles',
  },
  first_favorite_added: {
    howTo: 'Mets ton tout premier coup de cœur sur une pièce.',
    icon: 'Heart',
  },
  first_message_sent: {
    howTo: 'Envoie ton premier message à un vendeur.',
    icon: 'MessageCircle',
  },
  first_sale_received: {
    howTo: 'Reçois ta toute première vente.',
    icon: 'ShoppingBag',
  },
  tenth_listing_published: {
    howTo: 'Publie 10 pièces — tu deviens un vrai vendeur.',
    icon: 'Trophy',
  },
};

/**
 * Liste tous les achievements (débloqués + à débloquer) pour affichage
 * dans une page "Tes accomplissements".
 */
export function listAchievements(): AchievementPublic[] {
  return (Object.keys(CONFIG) as AchievementKey[]).map(key => {
    let unlockedAt: string | null = null;
    let unlocked = false;
    try {
      const raw = localStorage.getItem(STORAGE_PREFIX + key);
      if (raw) {
        unlocked = true;
        const ts = parseInt(raw, 10);
        if (Number.isFinite(ts)) {
          unlockedAt = new Date(ts).toISOString();
        }
      }
    } catch {
      /* localStorage indispo */
    }
    const meta = PUBLIC_META[key];
    const cfg = CONFIG[key];
    return {
      key,
      title: cfg.title,
      message: cfg.message,
      howTo: meta.howTo,
      icon: meta.icon,
      unlocked,
      unlockedAt,
    };
  });
}
